import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Activity, ArrowRight, Check, Share2 } from "lucide-react";
import { TOOLS, loadResults, healthScore, tierFor, RESULTS_EVENT } from "./toolkit";
import ShareDialog from "./ShareDialog";

const useResults = () => {
  const [r, setR] = useState(loadResults);
  useEffect(() => {
    const h = () => setR(loadResults());
    window.addEventListener(RESULTS_EVENT, h);
    window.addEventListener("storage", h);
    return () => { window.removeEventListener(RESULTS_EVENT, h); window.removeEventListener("storage", h); };
  }, []);
  return r;
};

const ToolChips = ({ r }) => (
  <div className="flex flex-wrap gap-2 mt-4">
    {TOOLS.map((t) => (
      <Link key={t.key} to={`/tools/${t.slug}`}
        className={`text-xs font-mono-plex px-2.5 py-1 rounded-sm border transition-colors ${r[t.key] ? "border-emerald-500/40 text-emerald-400" : "border-border text-muted-foreground hover:text-primary hover:border-primary"}`}
        data-testid={`health-chip-${t.key}`}>
        {r[t.key] && <Check className="w-3 h-3 inline mr-1" />}{t.title}
      </Link>
    ))}
  </div>
);

export default function HealthScore() {
  const r = useResults();
  const [shareOpen, setShareOpen] = useState(false);
  const used = TOOLS.filter((t) => r[t.key]).length;
  const { score, parts } = healthScore(r);
  const unlocked = used >= 2 && score !== null;
  const tier = unlocked ? tierFor(score) : null;

  return (
    <div className="dark border border-border bg-card text-foreground rounded-sm p-6 md:p-8" data-testid="health-score-card">
      <div className="flex items-center gap-2 overline text-primary"><Activity className="w-4 h-4" /> Your RuleAI Health Score</div>
      {unlocked ? (
        <div className="grid md:grid-cols-[auto_1fr_auto] gap-6 md:gap-10 items-center mt-4">
          <div>
            <div className="font-display font-black text-7xl tracking-tight" data-testid="health-score-value">{score}</div>
            <div className={`text-sm font-semibold ${tier.tone}`} data-testid="health-score-tier">{tier.label}</div>
          </div>
          <div className="space-y-3">
            {parts.map((p) => (
              <div key={p.key} data-testid={`health-part-${p.key}`}>
                <div className="flex justify-between text-xs font-mono-plex text-muted-foreground"><span>{p.label}</span><span>{p.raw}</span></div>
                <div className="h-2 bg-muted rounded-sm mt-1 overflow-hidden"><div className="h-full bg-primary transition-[width] duration-700" style={{ width: `${p.value}%` }} /></div>
              </div>
            ))}
            {parts.length < 3 && <p className="text-xs text-muted-foreground">Add {["OEE", "5S", "Downtime"].filter((l) => !parts.find((p) => p.label === l)).join(" + ")} for a complete score.</p>}
          </div>
          <div className="flex flex-col gap-2">
            <Link to="/register?source=health-score">
              <Button className="rounded-sm w-full bg-accent hover:bg-accent/90 text-white" data-testid="health-score-signup">Your full picture lives inside RuleAI <ArrowRight className="w-4 h-4 ml-2" /></Button>
            </Link>
            <Button variant="outline" className="rounded-sm" onClick={() => setShareOpen(true)} data-testid="health-score-share"><Share2 className="w-4 h-4 mr-2" />Share score</Button>
          </div>
        </div>
      ) : (
        <div className="mt-3">
          <div className="font-display font-bold text-xl md:text-2xl">Run any 2 tools to unlock a single plant health number.</div>
          <p className="text-sm text-muted-foreground mt-1" data-testid="health-score-locked">{used}/2 tools completed — results stay in your browser, no login.</p>
        </div>
      )}
      <ToolChips r={r} />
      {unlocked && (
        <ShareDialog open={shareOpen} onOpenChange={setShareOpen} card={{
          slug: "", tool: "RuleAI Health Score", scoreText: `${score}/100`, label: `Plant health · ${tier.label}`,
          bar: { value: score, target: 85, targetLabel: "World-class 85" },
          tagline: "Building a world-class plant with RuleAI",
          caption: `Our plant health score is ${score}/100 (${tier.label}). Measured free with RuleAI Systems.`,
        }} />
      )}
    </div>
  );
}
